// §26.4 (D-214) — the invitation cards, drawn on §19.4's above-every-frame layer.
//
// **Not a §13 notification, and not a dialog either.** §13 toasts for six seconds and then falls
// into the Center, which is exactly wrong for something whose value is being present when the other
// person looks up. A dialog would be worse: it is modal, so an invitation would stop the recipient
// working until they answered — which turns "want a game?" into an interruption they resent before
// they read it.
//
// So this is its own thing: it lives on the shell's layer where an app cannot draw (the reason
// §26.4 puts it in the kernel — an app that could draw here could forge one), it persists until
// answered or withdrawn, and it is **not modal**. `pointer-events` is on the cards alone, so the
// desktop underneath keeps taking clicks and the recipient can finish their sentence first.
//
// It is shell chrome rather than skin chrome, like the guest banner: it says something about the
// *session*, not about the desktop, so it reads the same under all three skins and uses only §6.2
// tokens.
import { formatOfferSize } from '@hangar/shared';
import { useKernel, useStoreValue } from '@/kernel/react';
import { MAX_VISIBLE_INVITES } from '@/kernel/shellui/invites';
import { COPY } from '@/copy';

export interface InviteLayerProps {
  /**
   * What **Join** does. Supplied by the composition root rather than resolved here, because
   * joining is M30's business (a play session) and this layer's job ends at "the user said yes".
   */
  onJoin?: (sessionId: string, gameId: string) => void;
  /**
   * §34.13 — what **Accept** and **Decline** do for a file offer. Supplied for the same reason:
   * accepting is a server round trip that lands a file and may be refused for space, and this
   * layer's job ends at "the user said yes".
   */
  onAnswerOffer?: (id: string, answer: 'accept' | 'decline') => void;
}

export function InviteLayer({ onJoin, onAnswerOffer }: InviteLayerProps) {
  const kernel = useKernel();
  const cards = useStoreValue(kernel.invites.store, (state) => state.cards);
  const offers = useStoreValue(kernel.invites.store, (state) => state.offers);

  if (cards.length === 0 && offers.length === 0) return null;

  const visible = cards.slice(0, MAX_VISIBLE_INVITES);
  const more = cards.length - visible.length;
  const visibleOffers = offers.slice(0, MAX_VISIBLE_INVITES);
  const moreOffers = offers.length - visibleOffers.length;

  return (
    <div className="invite-stack" data-testid="invite-stack">
      {/* §34.13 — file offers first: they are the ones that expire in seven days rather than when
          somebody closes a window, so they are the ones worth reading before the game invitations. */}
      {visibleOffers.map((card) => (
        <article className="invite-card" key={card.id} data-testid={`offer-${card.id}`}>
          <p className="invite-title">
            {COPY.offers.cardTitle(card.fromName, card.name, formatOfferSize(card.size))}
          </p>
          <div className="invite-actions">
            <button
              type="button"
              data-testid={`offer-accept-${card.id}`}
              onClick={() => onAnswerOffer?.(card.id, 'accept')}
            >
              {COPY.offers.accept}
            </button>
            <button
              type="button"
              data-testid={`offer-decline-${card.id}`}
              onClick={() => onAnswerOffer?.(card.id, 'decline')}
            >
              {COPY.offers.decline}
            </button>
          </div>
        </article>
      ))}
      {moreOffers > 0 ? (
        <p className="invite-more" data-testid="offer-more">
          {COPY.offers.more(moreOffers)}
        </p>
      ) : null}
      {visible.map((card) => (
        <article className="invite-card" key={card.sessionId} data-testid={`invite-${card.sessionId}`}>
          <p className="invite-title">{COPY.social.inviteTitle(card.fromName)}</p>
          <p className="invite-game">{card.gameName}</p>
          <div className="invite-actions">
            <button
              type="button"
              data-testid={`invite-join-${card.sessionId}`}
              onClick={() => {
                // Answering removes the card first, so a slow join cannot leave a button the user
                // has already pressed sitting there looking unpressed.
                const answered = kernel.invites.answer(card.sessionId);
                if (answered !== null) onJoin?.(answered.sessionId, answered.gameId);
              }}
            >
              {COPY.social.inviteJoin}
            </button>
            <button
              type="button"
              data-testid={`invite-decline-${card.sessionId}`}
              onClick={() => kernel.invites.answer(card.sessionId)}
            >
              {COPY.social.inviteDecline}
            </button>
          </div>
        </article>
      ))}
      {more > 0 ? (
        <p className="invite-more" data-testid="invite-more">
          {COPY.social.inviteMore(more)}
        </p>
      ) : null}
    </div>
  );
}
